import React from 'react';
import { Box, Stack, Typography } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import { ICampaignSales } from '../../../../redux/contest/types';
import { FONT_ENUM } from '../../../../types/global/fonts.types';
import { BRAND_LUXE_ERROR, BRAND_LUXE_PRIMARY } from '../../../../pages/BrandDashboard/brandDashboardTokens';
import { brandContestTableCellTextSx, midnight } from '../../../../pages/BrandDashboard/contestWizard/midnightStyles';
import { BrandContestSalesAvatar } from './BrandContestSalesAvatar';
import {
    brandSalesAmbientGlowBottomSx,
    brandSalesAmbientGlowTopSx,
    brandSalesCardGridSx,
    brandSalesCellCenterSx,
    brandSalesCellStartSx,
    brandSalesCompactCardSx,
    brandSalesCompactVisibleSx,
    brandSalesCurrencyChipSx,
    brandSalesDesktopVisibleSx,
    brandSalesDividerSx,
    brandSalesFieldLabelSx,
    brandSalesGlassCardSx,
} from './brandSalesCardLayout';
import { formatSaleAmount, formatSaleDiscount, saleStatusVisual } from './brandSalesCardShared';
import { pulseDot } from './submissionObsidianShared';

const VALUE_SX = {
    ...brandContestTableCellTextSx,
    fontSize: 14,
    color: midnight.onSurface,
} as const;

const METRIC_SX = {
    ...VALUE_SX,
    fontFamily: FONT_ENUM.MAIN_BOLD,
    fontWeight: 600,
    fontSize: 14,
    lineHeight: 1.2,
    fontVariantNumeric: 'tabular-nums',
} as const;

const HANDLE_SX = {
    fontFamily: '"Plus Jakarta Sans", Inter, sans-serif',
    fontWeight: 700,
    fontSize: { xs: 18, lg: 13 },
    lineHeight: 1.25,
    color: midnight.onSurface,
} as const;

const DATE_SX = {
    fontFamily: 'Inter, sans-serif',
    fontSize: 11,
    lineHeight: 1.3,
    color: midnight.onSurfaceVariant,
} as const;

const FieldLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <Typography component="span" sx={brandSalesFieldLabelSx}>
        {children}
    </Typography>
);

const StatusPill: React.FC<{ completed: boolean }> = ({ completed }) => {
    const status = saleStatusVisual(completed);
    return (
        <Box
            sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.75,
                px: 1.5,
                py: 0.5,
                borderRadius: 999,
                bgcolor: status.bg,
                color: status.color,
                border: `1px solid ${status.border}`,
                fontFamily: FONT_ENUM.MAIN_BOLD,
                fontWeight: 700,
                fontSize: 12,
                letterSpacing: '0.04em',
                whiteSpace: 'nowrap',
            }}>
            <Box
                sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    flexShrink: 0,
                    bgcolor: status.dot,
                    boxShadow: `0 0 8px ${status.dot}`,
                    ...(status.pulse ? { animation: `${pulseDot} 2s ease-in-out infinite` } : {}),
                }}
            />
            {status.label}
        </Box>
    );
};

const MetricBlock: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
    <Stack alignItems="center" spacing={0.5} sx={{ width: '100%', textAlign: 'center' }}>
        <FieldLabel>{label}</FieldLabel>
        {children}
    </Stack>
);

const DividerDots: React.FC = () => (
    <Box sx={brandSalesDividerSx}>
        <Box sx={{ height: '1px', flexGrow: 1, bgcolor: midnight.onSurface }} />
        <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: midnight.onSurface, flexShrink: 0 }} />
        <Box sx={{ height: '1px', flexGrow: 1, bgcolor: midnight.onSurface }} />
    </Box>
);

export interface BrandContestSalesCardProps {
    sale: ICampaignSales;
    dateDay: string;
    dateTime: string;
}

const BrandContestSalesCardCompact: React.FC<BrandContestSalesCardProps> = ({ sale, dateDay, dateTime }) => {
    const user = sale.created_by;
    const amount = formatSaleAmount(sale);
    const discount = formatSaleDiscount(sale);
    const hasDiscount = sale.is_discounted && discount !== '—';
    const currency = (sale.transaction_currency ?? '—').toUpperCase();

    return (
        <Box sx={[brandSalesCompactCardSx, brandSalesCompactVisibleSx] as SxProps<Theme>}>
            <Box sx={brandSalesAmbientGlowTopSx} />
            <Box sx={brandSalesAmbientGlowBottomSx} />

            <Stack alignItems="center" sx={{ position: 'relative', width: '100%', pt: 1, pb: 0.5 }}>
                <FieldLabel>User</FieldLabel>
                <Typography
                    sx={{
                        ...HANDLE_SX,
                        textAlign: 'center',
                        maxWidth: '100%',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                    }}>
                    @{user?.userName ?? 'unknown'}
                </Typography>
                <Box sx={{ my: 1.5 }}>
                    <BrandContestSalesAvatar user={user} completed={Boolean(sale.completed)} size={72} />
                </Box>
                <Stack alignItems="center" spacing={0.25} sx={{ opacity: 0.6 }}>
                    <Typography sx={DATE_SX}>{dateDay}</Typography>
                    <Typography sx={{ ...DATE_SX, fontSize: 10 }}>{dateTime}</Typography>
                </Stack>
            </Stack>

            <DividerDots />

            <Stack spacing={2.5} sx={{ position: 'relative', width: '100%', px: 0.5 }}>
                <MetricBlock label="Amount">
                    <Typography
                        sx={{
                            fontFamily: '"Plus Jakarta Sans", Inter, sans-serif',
                            fontWeight: 800,
                            fontSize: { xs: 28, sm: 32 },
                            lineHeight: 1.1,
                            color: BRAND_LUXE_PRIMARY,
                            textShadow: '0 0 12px rgba(255, 201, 101, 0.25)',
                            fontVariantNumeric: 'tabular-nums',
                        }}>
                        {amount}
                    </Typography>
                </MetricBlock>
                <MetricBlock label="Currency">
                    <Box sx={brandSalesCurrencyChipSx}>
                        <Typography
                            sx={{
                                fontFamily: 'Inter, sans-serif',
                                fontWeight: 600,
                                fontSize: 13,
                                letterSpacing: '0.04em',
                                color: midnight.onSurface,
                            }}>
                            {currency}
                        </Typography>
                    </Box>
                </MetricBlock>
                <MetricBlock label="Discount">
                    <Typography
                        sx={{
                            ...METRIC_SX,
                            fontSize: 18,
                            color: hasDiscount ? BRAND_LUXE_ERROR : midnight.onSurfaceVariant,
                        }}>
                        {discount}
                    </Typography>
                </MetricBlock>
                <MetricBlock label="Status">
                    <StatusPill completed={Boolean(sale.completed)} />
                </MetricBlock>
            </Stack>
        </Box>
    );
};

const BrandContestSalesCardDesktop: React.FC<BrandContestSalesCardProps> = ({ sale, dateDay, dateTime }) => {
    const user = sale.created_by;
    const amount = formatSaleAmount(sale);
    const discount = formatSaleDiscount(sale);
    const hasDiscount = sale.is_discounted && discount !== '—';
    const currency = (sale.transaction_currency ?? '—').toUpperCase();

    return (
        <Box sx={[brandSalesGlassCardSx, brandSalesDesktopVisibleSx] as SxProps<Theme>}>
            <Box sx={brandSalesCardGridSx}>
                <Stack
                    direction="row"
                    spacing={1.25}
                    alignItems="flex-start"
                    sx={{ ...brandSalesCellStartSx, width: '100%' }}>
                    <BrandContestSalesAvatar
                        user={user}
                        completed={Boolean(sale.completed)}
                        size={40}
                        showGradientRing={false}
                    />
                    <Stack sx={{ minWidth: 0, flex: 1 }}>
                        <Typography
                            sx={{
                                ...VALUE_SX,
                                fontWeight: 700,
                                fontSize: 13,
                                lineHeight: 1.25,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                maxWidth: '100%',
                            }}>
                            @{user?.userName ?? 'unknown'}
                        </Typography>
                        <Typography sx={{ ...DATE_SX, mt: 0.35 }}>{dateDay}</Typography>
                        <Typography sx={{ ...DATE_SX, fontSize: 10, color: midnight.outline, lineHeight: 1.2 }}>
                            {dateTime}
                        </Typography>
                    </Stack>
                </Stack>

                <Stack sx={brandSalesCellCenterSx}>
                    <Typography
                        sx={{
                            ...METRIC_SX,
                            color: BRAND_LUXE_PRIMARY,
                            textShadow: '0 0 8px rgba(255, 201, 101, 0.25)',
                        }}>
                        {amount}
                    </Typography>
                </Stack>

                <Stack sx={brandSalesCellCenterSx}>
                    <Box sx={brandSalesCurrencyChipSx}>
                        <Typography
                            sx={{
                                fontFamily: 'Inter, sans-serif',
                                fontWeight: 600,
                                fontSize: 11,
                                color: midnight.onSurface,
                            }}>
                            {currency}
                        </Typography>
                    </Box>
                </Stack>

                <Stack sx={brandSalesCellCenterSx}>
                    <Typography
                        sx={{
                            ...METRIC_SX,
                            color: hasDiscount ? BRAND_LUXE_ERROR : midnight.onSurfaceVariant,
                        }}>
                        {discount}
                    </Typography>
                </Stack>

                <Stack sx={brandSalesCellCenterSx}>
                    <StatusPill completed={Boolean(sale.completed)} />
                </Stack>
            </Box>
        </Box>
    );
};

const BrandContestSalesCard: React.FC<BrandContestSalesCardProps> = props => (
    <>
        <BrandContestSalesCardCompact {...props} />
        <BrandContestSalesCardDesktop {...props} />
    </>
);

export { BrandContestSalesCard };
